import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Can } from 'core/components/permissions/Can';
import {
  currencyFormatter,
  dateTimeFormatter,
  getFullName,
  isNumeric,
} from 'core/utils/commonUtils';
import { getOrderDiscountTypeName } from 'core/utils/orderUtils';
import useOrderDiscounts, { mutateOrderDiscounts } from 'hooks/swr/useOrderDiscounts';
import useResponsive from 'hooks/useResponsive';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { OrderDiscountTypeEnum } from 'models/enums/orderDiscountTypeEnum';
import { useContext, useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import * as orderDiscountService from 'services/orderDiscountService';
import StoreContext from 'stores/context/StoreContext';
import DiscountEditDialog from './DiscountEditDialog';
import type { GridColDef } from '@mui/x-data-grid';
import type { OrderDiscountInfo } from 'models/order/orderDiscountInfo';
import type { FC } from 'react';
import type { CreateOrUpdateOrderDiscountRequest } from 'services/dtoModels/order/createOrUpdateOrderDiscountRequest';
import { useModuleAccess } from 'hooks/useModuleAccess';

const OrderDiscountsEdit: FC<OrderDiscountsEditProps> = observer(({ orderId }) => {
  const isSmScreen = useResponsive('down', 'sm');
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editedDiscount, setEditedDiscount] = useState<OrderDiscountInfo>();
  const confirm = useConfirm();
  const { isModuleEnabled } = useModuleAccess();

  const { data: discounts, isLoading } = useOrderDiscounts(
    orderId,
    isNumeric(orderId ?? '')
  );

  const { userStore, notifyStore } = useContext(StoreContext);

  const handleDiscountDialogCancel: VoidFunction = () => {
    setDiscountDialogOpen(false);
    setEditedDiscount(undefined);
  };

  const handleDiscountDialogSave = async (
    discount: OrderDiscountInfo
  ): Promise<boolean> => {
    const isNew = !isNil(editedDiscount) && editedDiscount.id <= 0;

    const request: CreateOrUpdateOrderDiscountRequest = {
      remark: discount.remark,
      sum: discount.sum,
      type: discount.type,
    };

    try {
      const query = isNew
        ? orderDiscountService.createOrderDiscountAsync(+(orderId ?? 0), request)
        : orderDiscountService.updateOrderDiscountAsync(
            +(orderId ?? 0),
            discount.id,
            request
          );

      await query;

      await mutateOrderDiscounts(orderId);

      setDiscountDialogOpen(false);
      setEditedDiscount(undefined);

      notifyStore.showNotify(isNew ? 'Скидка добавлена' : 'Скидка обновлена', 'success');

      return true;
    } catch {
      notifyStore.showNotify('Ошибка при сохранении скидки', 'error');
      return false;
    }
  };

  const handleAddDiscount = (): void => {
    setEditedDiscount({
      id: -Date.now(),
      dateCreate: new Date(),
      orderId: 0,
      remark: '',
      sum: 0,
      type: OrderDiscountTypeEnum.FixedAmount,
      userId: userStore.userData?.id ?? 0,
      user: {
        id: userStore.userData?.id ?? 0,
        fullName: !isNil(userStore.userData) ? getFullName(userStore.userData) : '',
      },
    });
    setDiscountDialogOpen(true);
  };

  const handleEdit = (discount: OrderDiscountInfo): void => {
    setEditedDiscount(discount);
    setDiscountDialogOpen(true);
  };

  const handleDelete = async (discount: OrderDiscountInfo): Promise<void> => {
    try {
      await confirm({
        description: `Удалить скидку ${formatDiscountValue(discount)}${
          discount.remark ? ` (${discount.remark})` : ''
        }?`,
      });

      await orderDiscountService.deleteOrderDiscountAsync(orderId, discount.id);
      await mutateOrderDiscounts(orderId);

      notifyStore.showNotify('Скидка удалена', 'success');
    } catch (error) {
      if (error !== undefined) {
        notifyStore.showNotify('Ошибка при удалении скидки', 'error');
      }
    }
  };

  const formatDiscountValue = (discount: OrderDiscountInfo): string => {
    if (discount.type === OrderDiscountTypeEnum.Percentage) {
      return `${discount.sum}%`;
    }
    return currencyFormatter.format(discount.sum);
  };

  const canUpdate = userStore.ability?.can('update', 'OrderDiscounts') ?? false;

  const columns: Array<GridColDef<OrderDiscountInfo>> = [
    {
      field: 'dateCreate',
      headerName: 'Дата создания',
      valueFormatter: (value) => dateTimeFormatter.format(value),
      minWidth: 150,
    },
    {
      field: 'sum',
      headerName: 'Размер скидки',
      valueGetter: (_, row) => formatDiscountValue(row),
      minWidth: 120,
    },
    // {
    //   field: 'type',
    //   headerName: 'Тип',
    //   valueGetter: (value) => getOrderDiscountTypeName(value),
    //   flex: 0.3,
    // },
    {
      field: 'owner',
      headerName: 'Создал',
      valueGetter: (_, row) => row.user?.fullName ?? '',
      flex: 0.6,
    },
    { field: 'remark', headerName: 'Примечание', flex: 1 },
  ];

  if (canUpdate) {
    columns.push({
      field: 'actions',
      maxWidth: 80,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          key={`edit-${params.row.id}`}
          onClick={() => {
            handleEdit(params.row);
          }}
          label='Редактировать'
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          key={`delete-${params.row.id}`}
          onClick={() => {
            void handleDelete(params.row);
          }}
          label='Удалить'
        />,
      ],
    });
  }

  // Проверяем доступность модуля скидок
  if (!isModuleEnabled('discounts')) {
    return null;
  }

  if (isLoading) {
    return <Skeleton variant='rounded' height={200} />;
  }

  return (
    <Stack spacing={2}>
      <Can I='create' a='OrderDiscounts'>
        <Box>
          <Button variant='outlined' startIcon={<AddIcon />} onClick={handleAddDiscount}>
            Добавить скидку
          </Button>
        </Box>
      </Can>

      {isSmScreen ? (
        <List disablePadding>
          {discounts?.map((discount) => (
            <ListItem
              key={discount.id}
              disablePadding
              secondaryAction={
                <Can I='update' a='OrderDiscounts'>
                  <Stack direction='row' spacing={1}>
                    <IconButton
                      edge='end'
                      aria-label='edit'
                      onClick={() => {
                        handleEdit(discount);
                      }}>
                      <EditIcon />
                    </IconButton>
                    <Can I='delete' a='OrderDiscounts'>
                      <IconButton
                        edge='end'
                        aria-label='delete'
                        onClick={() => {
                          void handleDelete(discount);
                        }}>
                        <DeleteIcon />
                      </IconButton>
                    </Can>
                  </Stack>
                </Can>
              }>
              <ListItemText
                primary={`${formatDiscountValue(discount)} от ${dateTimeFormatter.format(
                  discount.dateCreate
                )}`}
                secondary={`${getOrderDiscountTypeName(discount.type)}${
                  discount.remark ? `. ${discount.remark}` : ''
                }`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <DataGrid
          rows={discounts ?? []}
          columns={columns}
          hideFooter
          disableRowSelectionOnClick
          autoHeight
        />
      )}

      {editedDiscount && (
        <DiscountEditDialog
          open={discountDialogOpen}
          discount={editedDiscount}
          onCancel={handleDiscountDialogCancel}
          onSave={handleDiscountDialogSave}
        />
      )}
    </Stack>
  );
});

export interface OrderDiscountsEditProps {
  orderId: number;
}

export default OrderDiscountsEdit;
